import { create } from 'zustand';
import type { Message, ChatWidgetState, ChatConfig } from '@/types/chat';

interface ChatStore extends ChatWidgetState {
  config: ChatConfig | null;
  
  // Actions
  setConfig: (config: ChatConfig) => void;
  setOpen: (isOpen: boolean) => void;
  toggleOpen: () => void;
  setMinimized: (isMinimized: boolean) => void;
  addMessage: (message: Message) => void;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  setMessages: (messages: Message[]) => void;
  clearMessages: () => void;
  setTyping: (isTyping: boolean) => void;
  setConnected: (isConnected: boolean) => void;
  setError: (error: string | null) => void;
  setSessionId: (sessionId: string | null) => void;
  reset: () => void;
}

const initialState: ChatWidgetState = {
  isOpen: false,
  isMinimized: false,
  messages: [],
  isTyping: false,
  isConnected: false,
  error: null,
  sessionId: null,
};

// Load initial state from localStorage
const loadPersistedState = (): Partial<ChatWidgetState> => {
  try {
    // Only load persisted state for embedded widget mode
    // Don't persist for hosted chat pages (they should start fresh each time)
    const isHostedChat = window.location.pathname.startsWith('/chat/');
    if (isHostedChat) {
      return {};
    }

    const stored = localStorage.getItem('chat-widget-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        sessionId: parsed.sessionId,
        messages: parsed.messages || [],
      };
    }
  } catch (error) {
    // console.error('Failed to load persisted state:', error);
  }
  return {};
};

// Save state to localStorage
const persistState = (state: ChatWidgetState) => {
  try {
    // Only persist state for embedded widget mode
    // Don't persist for hosted chat pages (they should start fresh each time)
    const isHostedChat = window.location.pathname.startsWith('/chat/');
    if (isHostedChat) {
      return;
    }

    localStorage.setItem('chat-widget-storage', JSON.stringify({
      sessionId: state.sessionId,
      messages: state.messages,
    }));
  } catch (error) {
    // console.error('Failed to persist state:', error);
  }
};

export const useChatStore = create<ChatStore>((set, get) => ({
  ...initialState,
  ...loadPersistedState(),
  config: null,

  setConfig: (config) => set({ config }),

  setOpen: (isOpen) => {
    set({ isOpen });
    if (isOpen) {
      get().config?.onOpen?.();
    } else {
      get().config?.onClose?.();
    }
  },

  toggleOpen: () => {
    const { isOpen } = get();
    get().setOpen(!isOpen);
  },

  setMinimized: (isMinimized) => set({ isMinimized }),

  addMessage: (message) => {
    set((state) => {
      const newState = {
        messages: [...state.messages, message],
      };
      persistState({ ...state, ...newState });
      return newState;
    });
  },

  updateMessage: (id, updates) => {
    set((state) => {
      const newState = {
        messages: state.messages.map((msg) =>
          msg.id === id ? { ...msg, ...updates } : msg
        ),
      };
      persistState({ ...state, ...newState });
      return newState;
    });
  },

  setMessages: (messages) => {
    set((state) => {
      const newState = { messages };
      persistState({ ...state, ...newState });
      return newState;
    });
  },

  clearMessages: () => {
    set((state) => {
      const newState = { messages: [] };
      persistState({ ...state, ...newState });
      return newState;
    });
  },

  setTyping: (isTyping) => set({ isTyping }),

  setConnected: (isConnected) => set({ isConnected }),

  setError: (error) => {
    set({ error });
    if (error) {
      get().config?.onError?.(new Error(error));
    }
  },

  setSessionId: (sessionId) => {
    set((state) => {
      const newState = { sessionId };
      persistState({ ...state, ...newState });
      return newState;
    });
  },

  reset: () => {
    set(initialState);
    localStorage.removeItem('chat-widget-storage');
  },
}));
